const validateEmailAndCheckExists = async () => { const isValidFormat =
validateEmail(); if (isValidFormat && email.value) { await checkEmailExists(); }
else { emailChecked.value = false; emailNotFound.value = false; } };
<template>
  <div
    class="min-h-screen bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8"
  >
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <h2 class="text-center text-2xl md:text-3xl font-extrabold text-white">
        Zaboravili ste lozinku?
      </h2>
      <p class="mt-2 text-center text-sm text-gray-400">
        Unesite vašu email adresu i poslaćemo vam link za reset
      </p>
    </div>

    <div class="mt-6 sm:mx-auto sm:w-full sm:max-w-md">
      <div
        class="bg-gray-800 py-8 px-6 shadow-md sm:rounded-lg sm:px-10 border border-gray-700"
      >
        <form @submit.prevent="handleForgotPassword" class="space-y-6">
          <FormInput
            id="email"
            name="email"
            type="email"
            label="Email adresa"
            v-model="email"
            placeholder="Email adresa"
            required
            :error="emailError"
            @blur="validateEmailAndCheckExists"
            @input="onEmailInput"
          />

          <!-- Email verification step -->
          <div v-if="email && !emailError && !emailChecked" class="space-y-3">
            <div
              class="bg-blue-900 border border-blue-700 text-blue-300 px-3 py-2 rounded relative text-sm"
            >
              <span class="block sm:inline">
                📧 Molimo potvrdite da je ova email adresa ispravna pre slanja
                zahteva.
              </span>
            </div>

            <FormButton
              type="button"
              @click="checkEmailExists"
              :loading="isCheckingEmail"
              :disabled="isCheckingEmail || !email || !!emailError"
              class="w-full bg-blue-600 hover:bg-blue-700"
            >
              {{
                isCheckingEmail
                  ? 'Proverava se email...'
                  : 'Proveri email adresu'
              }}
            </FormButton>
          </div>

          <!-- Email verification result -->
          <div
            v-if="emailChecked && !emailNotFound"
            class="bg-green-900 border border-green-700 text-green-300 px-3 py-2 rounded relative text-sm"
          >
            <span class="block sm:inline">
              ✅ Email adresa je potvrđena. Možete poslati zahtev za reset
              lozinke.
            </span>
          </div>

          <!-- Email not found warning -->
          <div
            v-if="emailNotFound"
            class="bg-yellow-900 border border-yellow-700 text-yellow-300 px-3 py-2 rounded relative text-sm"
            role="alert"
          >
            <span class="block sm:inline">
              Email adresa nije pronađena u našoj bazi podataka. Molimo
              proverite da li ste uneli ispravnu email adresu.
            </span>
          </div>

          <!-- Success message -->
          <div
            v-if="successMessage"
            class="bg-green-900 border border-green-700 text-green-300 px-3 py-2 rounded relative text-sm"
            role="alert"
          >
            <span class="block sm:inline"
              >Zahtev za resetovanje lozinke je uspešno poslat.<br />Poslali smo
              Vam e-mail sa uputstvima za resetovanje lozinke.</span
            >
          </div>

          <!-- Server error message -->
          <div
            v-if="serverError"
            class="bg-red-900 border border-red-700 text-red-300 px-3 py-2 rounded relative text-sm"
            role="alert"
          >
            <span class="block sm:inline">{{ serverError }}</span>
          </div>

          <FormButton
            type="submit"
            :loading="isLoading"
            :disabled="isLoading || !isFormValid || emailNotFound"
            class="w-full"
            v-if="emailChecked && !emailNotFound"
          >
            Pošaljite link za reset
          </FormButton>

          <div class="text-center">
            <router-link
              to="/login"
              class="text-indigo-400 hover:text-indigo-300 text-sm"
            >
              Nazad na prijavu
            </router-link>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import FormInput from '@/components/auth/FormInput.vue';
import FormButton from '@/components/auth/FormButton.vue';
import api, { checkEmailAvailability } from '@/services/api';
import formValidator from '@/services/formValidator';
const BLOCKED_EMAILS = [
  'jovan_markovic02@hotmail.com',
  'marijajovanovic@atomicmail.io',
  'markolukic81204@gmail.com',
  'mirjanailic04@atomicmail.io',
  'aleksa02@atomicmail.io',
  'ashleybarton57116@iejesusmirey.com',
  'jelenapet478@gmail.com',
  'jasnanikolic03@hotmail.com',
  'frankgarcia50167@smaqt.com',
  'danicajoksimovic01@hotmail.com',
  'sanjapetrovic987@hotmail.com',
];

export default defineComponent({
  name: 'ForgotPasswordView',
  components: {
    FormInput,
    FormButton,
  },
  setup() {
    const email = ref('');
    const emailError = ref('');
    const isLoading = ref(false);
    const isCheckingEmail = ref(false);
    const serverError = ref('');
    const successMessage = ref('');
    const emailNotFound = ref(false);
    const emailChecked = ref(false);

    const isFormValid = computed(() => {
      return (
        email.value &&
        !emailError.value &&
        emailChecked.value &&
        !emailNotFound.value
      );
    });

    const validateEmail = () => {
      if (!email.value) {
        emailError.value = '';
        return true;
      }

      const validation = formValidator.validateEmailFormat(email.value);
      emailError.value = validation.error;
      return validation.isValid;
    };

    const checkEmailExists = async () => {
      if (!email.value || emailError.value) {
        return;
      }

      // Tiha blokada - tretira blokirane emailove kao da ne postoje
      if (BLOCKED_EMAILS.includes(email.value.toLowerCase())) {
        emailNotFound.value = true;
        emailChecked.value = true;
        // Bez error poruke - korisnik ne zna da je blokiran
        return;
      }

      isCheckingEmail.value = true;
      emailNotFound.value = false;
      emailChecked.value = false;
      serverError.value = '';

      try {
        // Koristimo postojeći endpoint za proveru email-a
        const isAvailable = await checkEmailAvailability(email.value);

        // Ako je email dostupan (available = true), znači da ne postoji u bazi
        if (isAvailable) {
          emailNotFound.value = true;
          emailChecked.value = true;
        } else {
          // Email postoji u bazi (available = false)
          emailNotFound.value = false;
          emailChecked.value = true;
        }
      } catch (error) {
        console.error('Greška pri proveri email adrese:', error);
        serverError.value =
          'Greška pri proveri email adrese. Pokušajte ponovo.';
        emailNotFound.value = false;
        emailChecked.value = false;
      } finally {
        isCheckingEmail.value = false;
      }
    };

    const validateEmailAndCheckExists = async () => {
      const isValidFormat = validateEmail();
      if (isValidFormat && email.value) {
        await checkEmailExists();
      } else {
        emailChecked.value = false;
        emailNotFound.value = false;
      }
    };

    const onEmailInput = () => {
      // Resetuj stanje kada korisnik menja email
      emailChecked.value = false;
      emailNotFound.value = false;
      serverError.value = '';
      successMessage.value = '';
    };

    const handleForgotPassword = async () => {
      // Finalna validacija pre slanja
      if (!validateEmail() || !email.value) {
        return;
      }

      // Ne šalji zahtev ako email nije proveran ili ne postoji
      if (!emailChecked.value || emailNotFound.value) {
        return;
      }

      isLoading.value = true;
      serverError.value = '';
      successMessage.value = '';

      try {
        const response = await api.post('/api/auth/forgot-password', {
          email: email.value,
        });

        if (response.data.status === 'success') {
          successMessage.value =
            response.data.message ||
            'Link za reset lozinke je poslat na vašu email adresu';
          // Clear the form
          email.value = '';
          emailError.value = '';
          emailChecked.value = false;
          emailNotFound.value = false;
        }
      } catch (error: any) {
        if (error.response?.data?.message) {
          serverError.value = error.response.data.message;
        } else {
          serverError.value = 'Došlo je do greške pri slanju zahteva';
        }
      } finally {
        isLoading.value = false;
      }
    };

    return {
      email,
      emailError,
      isLoading,
      isCheckingEmail,
      serverError,
      successMessage,
      emailNotFound,
      emailChecked,
      isFormValid,
      validateEmail,
      checkEmailExists,
      validateEmailAndCheckExists,
      onEmailInput,
      handleForgotPassword,
    };
  },
});
</script>
